import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteAdminCopilotConversation,
  getAdminCopilotConversation,
  listAdminCopilotConversations,
  sendAdminCopilotMessage,
  type AdminCopilotMessage,
  type AdminCopilotSummary,
} from "../services/adminCopilotService";

function nextId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `msg-${Date.now()}-${Math.random()}`;
}

export function useAdminCopilot(options: {
  tenantId: string;
  locale: "en" | "ar";
  enabled: boolean;
}) {
  const { tenantId, locale, enabled } = options;
  const [conversationId, setConversationId] = useState<string>();
  const [messages, setMessages] = useState<AdminCopilotMessage[]>([]);
  const [history, setHistory] = useState<AdminCopilotSummary[]>([]);
  const [pending, setPending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const conversationIdRef = useRef<string | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  const refreshHistory = useCallback(async () => {
    if (!enabled || !tenantId) return;
    setHistoryLoading(true);
    try {
      setHistory(await listAdminCopilotConversations({ tenantId, locale }));
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [enabled, locale, tenantId]);

  const resetActiveChat = useCallback(() => {
    abortRef.current?.abort();
    conversationIdRef.current = undefined;
    setConversationId(undefined);
    setMessages([]);
    setPending(false);
    setReadOnly(false);
    setErrorCode(null);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (text: string, route?: string) => {
      const message = text.trim();
      if (!enabled || !tenantId || !message || pending || readOnly) return;
      setErrorCode(null);
      setMessages((current) => [...current, { id: nextId(), role: "user", text: message }]);
      setPending(true);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const result = await sendAdminCopilotMessage({
          tenantId,
          message,
          locale,
          conversationId: conversationIdRef.current,
          route,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        if (result.conversationId) {
          conversationIdRef.current = result.conversationId;
          setConversationId(result.conversationId);
        }
        setMessages((current) => [
          ...current,
          { id: nextId(), role: "assistant", text: result.text },
        ]);
        void refreshHistory();
      } catch (error) {
        if (controller.signal.aborted) return;
        const code =
          error && typeof error === "object" && "code" in error
            ? String((error as { code?: string }).code)
            : "UNKNOWN";
        setErrorCode(code);
      } finally {
        if (!controller.signal.aborted) setPending(false);
      }
    },
    [enabled, locale, pending, readOnly, refreshHistory, tenantId],
  );

  const openConversation = useCallback(
    async (id: string) => {
      if (!enabled || !tenantId) return;
      setErrorCode(null);
      try {
        const detail = await getAdminCopilotConversation({ tenantId, id, locale });
        setConversationId(detail.id);
        conversationIdRef.current = detail.id;
        setMessages(detail.messages);
        setReadOnly(true);
      } catch {
        setErrorCode("NOT_FOUND");
      }
    },
    [enabled, locale, tenantId],
  );

  const removeConversation = useCallback(
    async (id: string) => {
      if (!tenantId) return;
      await deleteAdminCopilotConversation({ tenantId, id });
      if (conversationIdRef.current === id) resetActiveChat();
      void refreshHistory();
    },
    [refreshHistory, resetActiveChat, tenantId],
  );

  return {
    conversationId,
    messages,
    history,
    historyLoading,
    pending,
    readOnly,
    errorCode,
    send,
    refreshHistory,
    resetActiveChat,
    openConversation,
    removeConversation,
  };
}
